// ░█▀▀▄ ░█▀▀▀█ 　 ░█▄─░█ ░█▀▀▀█ ▀▀█▀▀ 　 ░█▀▀▀ ░█▀▀▄ ▀█▀ ▀▀█▀▀
// ░█─░█ ░█──░█ 　 ░█░█░█ ░█──░█ ─░█── 　 ░█▀▀▀ ░█─░█ ░█─ ─░█──
// ░█▄▄▀ ░█▄▄▄█ 　 ░█──▀█ ░█▄▄▄█ ─░█── 　 ░█▄▄▄ ░█▄▄▀ ▄█▄ ─░█──
// The component is made to be used flexibly. But if you REALLY MUST update edit the component,
// please add `-custom` on the change log file (e.g. `1.0.0-custom.md`) and log the changes

// ========================================================================================================================================
// Hooks
// ========================================================================================================================================

import { useState, useEffect, useCallback, useLayoutEffect } from "react"
import moment from "moment-timezone"
import { useInView } from "react-intersection-observer"
import { useRouter } from "next/router"

// ==========================================================================
// Count Down
// ==========================================================================

const prependZero = (number, limit) => {
  return number < limit ? `0${number}` : number
}

export const useCountdown = ({ startDate, endDate }) => {
  const [isStart, setStart] = useState(false)
  const [isEnded, setEnded] = useState(false)
  const [countdown, setCountdown] = useState({
    days: undefined,
    hours: undefined,
    minutes: undefined,
    seconds: undefined,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const eventStart = moment(startDate)
      const eventEnded = moment(endDate)
      const now = moment()

      const duration = moment.duration(eventStart.diff(now))
      const days = prependZero(duration.days(), 10)
      const hours = prependZero(duration.hours(), 10)
      const minutes = prependZero(duration.minutes(), 10)
      const seconds = prependZero(duration.seconds(), 10)

      if (eventEnded.valueOf() < now.valueOf()) {
        setEnded(true)
      } else if (eventStart.valueOf() < now.valueOf()) {
        setStart(true)
      } else {
        setCountdown({
          days,
          hours,
          minutes,
          seconds,
        })
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  return [isStart, isEnded, countdown]
}

// ==========================================================================
// Scroll Anim
// ==========================================================================

/**
 *
 * @param {*} animation
 * @returns
 *
 * e.g .fadeInUp, .fadeInDown, etc.
 * Please check base/animations.scss for complete list of animations
 */

export const useScrollAnim = (
  threshold = [0.125, 0.5, 0.75],
  animation = "fadeInUp",
  triggerOnce = true
) => {
  const [ref, inView] = useInView({
    threshold,
    triggerOnce,
  })

  const [animate, setAnimate] = useState(() => {
    return {
      text: `scroll-${animation}`,
      hasSet: false,
    }
  })

  const anim = useCallback(
    (delay, replaceAnim = null) => {
      if (replaceAnim) {
        return animate.hasSet
          ? `scroll-${replaceAnim} ${replaceAnim}` + ` d${delay}`
          : `scroll-${replaceAnim}`
      } else {
        return animate.hasSet
          ? `${animate.text} ${animation}` + ` d${delay}`
          : animate.text
      }
    },
    [animate]
  )

  useEffect(() => {
    if (inView) {
      setAnimate((prevState) => {
        return {
          ...prevState,
          hasSet: true,
        }
      })
    }
  }, [inView])

  return [ref, anim, inView]
}

export const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener("mousedown", listener)
    document.addEventListener("touchstart", listener)
    return () => {
      document.removeEventListener("mousedown", listener)
      document.removeEventListener("touchstart", listener)
    }
  }, [ref, handler])
}

export const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  })
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener("resize", handleResize)
    handleResize()
    return () => window.removeEventListener("resize", handleResize)
  }, [])
  return windowSize
}

export const refreshData = (Router) => {
  Router.replace(Router.asPath)
}

// ==========================================================================
// Scroll Depth Tracking
// ==========================================================================
import { sendDataLayer } from "lib/function/datalayer"

const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100]

export function useScrollDepth() {
  const router = useRouter()
  useEffect(() => {
    const thresholdsTriggered = new Set()

    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight
      const scrollPercentage = Math.floor((scrollTop / scrollHeight) * 100)

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (
          scrollPercentage >= threshold &&
          !thresholdsTriggered.has(threshold)
        ) {
          sendDataLayer({
            event: "scroll_tracking",
            percent_scroll: `${threshold}`,
          })
          thresholdsTriggered.add(threshold)
        }
      })
    }

    window.addEventListener("scroll", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
    }
  }, [router.pathname]) // Re-run effect on route change
}
